/**
 * Hook for uploading artworks — local blob for instant preview + background R2 upload.
 *
 * Usage:
 *   const { upload, isUploading, error } = useArtworkUpload();
 *   const result = await upload(file, dimensions);
 */

import { useState, useCallback } from 'react';
import { storeLocalImage, setRemoteImageUrl } from '../stores/projectStore';
import { uploadArtwork, getArtworkImageUrl, ApiError } from '../lib/api-client';

const isDevMode = !import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

/** Upload result returned to the caller */
export interface UploadResult {
  /** Artwork ID (local or from API) */
  artworkId: string;
  /** Image dimensions */
  width: number;
  height: number;
  /** Whether the upload was synced to the API */
  synced: boolean;
}

/** Hook for artwork uploading with local preview + background API sync */
export function useArtworkUpload(): {
  upload: (file: File, dimensions: { width: number; height: number }) => Promise<UploadResult>;
  isUploading: boolean;
  error: string | null;
} {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(
    async (file: File, dimensions: { width: number; height: number }): Promise<UploadResult> => {
      setIsUploading(true);
      setError(null);

      // Always create a local blob URL for instant preview
      const localId = `art_${Date.now()}`;
      storeLocalImage(localId, file);

      // In dev mode, skip API upload
      if (isDevMode) {
        setIsUploading(false);
        return {
          artworkId: localId,
          width: dimensions.width,
          height: dimensions.height,
          synced: false,
        };
      }

      // Try API upload in background
      try {
        const result = await uploadArtwork(file);

        // Store remote URL for fallback when blob expires
        setRemoteImageUrl(result.artworkId, getArtworkImageUrl(result.artworkId, 'original'));

        // Also keep local blob mapped to the real artworkId for fast rendering
        storeLocalImage(result.artworkId, file);

        setIsUploading(false);
        return {
          artworkId: result.artworkId,
          width: result.width,
          height: result.height,
          synced: true,
        };
      } catch (err) {
        // API upload failed — fall back to local-only mode
        const message = err instanceof ApiError ? err.message : 'Upload failed';
        setError(message);
        setIsUploading(false);

        return {
          artworkId: localId,
          width: dimensions.width,
          height: dimensions.height,
          synced: false,
        };
      }
    },
    [],
  );

  return { upload, isUploading, error };
}
